/**
 * NavigationLogCard - Log navigation events, waypoints, course changes
 */

import React, { useState } from 'react';
import { MapPin, ChevronDown, ChevronUp, Plus, X } from 'lucide-react';

const NavigationLogCard = ({ 
  navigationEntries,
  setNavigationEntries,
  isExpanded,
  onToggle,
  onAddLogEntry,
  activeVoyage
}) => {
  const [newEntry, setNewEntry] = useState({
    type: 'waypoint',
    description: '',
    position: '',
    time: new Date().toTimeString().slice(0, 5)
  });

  const navEventTypes = [
    { value: 'waypoint', label: 'Waypoint Passed' },
    { value: 'course_change', label: 'Course Change' },
    { value: 'speed_change', label: 'Speed Change' },
    { value: 'traffic', label: 'Traffic Situation' },
    { value: 'nav_warning', label: 'Navigation Warning' },
    { value: 'restricted_area', label: 'Restricted Area' },
    { value: 'pilot_boarding', label: 'Pilot Boarding' },
    { value: 'pilot_disembark', label: 'Pilot Disembark' },
    { value: 'vts_report', label: 'VTS Report' },
    { value: 'other', label: 'Other' }
  ];

  const handleAddEntry = () => {
    if (!newEntry.description) {
      alert('Please enter a description');
      return;
    }

    const navEntry = {
      ...newEntry,
      id: Date.now(),
      timestamp: new Date().toISOString()
    };

    // Add to local state
    const updated = [...navigationEntries, navEntry];
    setNavigationEntries(updated);

    // Create log entry
    const logEntry = {
      type: 'NAV_EVENT',
      category: 'navigation',
      summary: `${navEventTypes.find(t => t.value === newEntry.type)?.label}: ${newEntry.description}`,
      details: navEntry,
      timestamp: new Date().toISOString(),
      voyageId: activeVoyage?.id
    };

    onAddLogEntry(logEntry);

    // Reset form
    setNewEntry({
      type: 'waypoint',
      description: '',
      position: '',
      time: new Date().toTimeString().slice(0, 5)
    });
  };

  const removeEntry = (id) => {
    setNavigationEntries(navigationEntries.filter(e => e.id !== id));
  };

  return (
    <div className="rounded-lg border-2 bg-white border-gray-200">
      <div 
        onClick={onToggle}
        className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <MapPin className="h-5 w-5 text-gray-600" />
            <div>
              <h4 className="text-sm font-medium">Navigation Log</h4>
              <p className="text-xs text-gray-500">
                {navigationEntries.length} navigation events recorded
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {navigationEntries.length > 0 && (
              <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs">
                {navigationEntries.length}
              </span>
            )}
            {isExpanded ? 
              <ChevronUp className="h-5 w-5 text-gray-400" /> : 
              <ChevronDown className="h-5 w-5 text-gray-400" />
            }
          </div>
        </div>
      </div>

      {isExpanded && (
        <div className="px-4 pb-4 border-t border-gray-100">
          <div className="space-y-4 mt-4">
            {/* Add New Entry Form */}
            <div className="bg-gray-50 p-3 rounded-lg space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Event Type
                  </label>
                  <select
                    value={newEntry.type}
                    onChange={(e) => setNewEntry(prev => ({ ...prev, type: e.target.value }))}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    {navEventTypes.map(type => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Time
                  </label>
                  <input
                    type="time"
                    value={newEntry.time}
                    onChange={(e) => setNewEntry(prev => ({ ...prev, time: e.target.value }))}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Description
                </label>
                <input
                  type="text"
                  value={newEntry.description}
                  onChange={(e) => setNewEntry(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="e.g., Passed Bramble Bank buoy to starboard"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Position (optional)
                </label>
                <input
                  type="text"
                  value={newEntry.position}
                  onChange={(e) => setNewEntry(prev => ({ ...prev, position: e.target.value }))}
                  placeholder="e.g., 50.3755 N, 004.1427 W"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <button
                onClick={handleAddEntry}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium flex items-center justify-center gap-2"
              >
                <Plus className="h-4 w-4" />
                Add Navigation Event
              </button>
            </div>

            {/* Navigation Entries List */}
            {navigationEntries.length > 0 ? (
              <div className="space-y-2">
                <h5 className="text-xs font-medium text-gray-700">Navigation Events</h5>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {navigationEntries.map((entry) => (
                    <div key={entry.id} className="bg-white border border-gray-200 rounded-lg p-3">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-medium text-blue-600">
                              {navEventTypes.find(t => t.value === entry.type)?.label}
                            </span>
                            <span className="text-xs text-gray-400">
                              {entry.time || new Date(entry.timestamp).toLocaleTimeString()}
                            </span>
                          </div>
                          <p className="text-sm text-gray-700 mt-1">{entry.description}</p>
                          {entry.position && (
                            <p className="text-xs text-gray-500 mt-1">📍 {entry.position}</p>
                          )}
                        </div>
                        <button
                          onClick={() => removeEntry(entry.id)}
                          className="p-1 text-gray-400 hover:text-red-600"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-500 text-center py-4">
                No navigation events recorded yet
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NavigationLogCard;